import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenVaccinationsIndicatorC6Data } from "@/services/esus/indicator/esf/c6/getDetailedCitizensVaccinationsIndicatorC6";

import { IndicatorEsfC6CitizensVaccinationsFilters } from "./IndicatorEsfC6CitizensVaccinationsFilters";
import {
	getCitizenVaccinationStatusDescription,
	IndicatorEsfC6CitizensVaccinationsTable,
} from "./IndicatorEsfC6CitizensVaccinationsTable";
import { IndicatorEsfC6CitizensVaccinationsHistory } from "./IndicatorEsfC6CitizensVaccinationsHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC6CitizensVaccinationsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensVaccinations: Paginated<DetailedCitizenVaccinationsIndicatorC6Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensVaccinationsIndicatorC6: () => Promise<
		DetailedCitizenVaccinationsIndicatorC6Data[]
	>;
};

export function IndicatorEsfC6CitizensVaccinations({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensVaccinations,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensVaccinationsIndicatorC6,
}: Readonly<IndicatorEsfC6CitizensVaccinationsProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithVaccinations, setCitizenWithVaccinations] =
		useState<DetailedCitizenVaccinationsIndicatorC6Data>(
			new DetailedCitizenVaccinationsIndicatorC6Data()
		);
	const [isLoadingCitizenWithVaccinations, setIsLoadingCitizenWithVaccinations] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(
		citizenVaccinations: DetailedCitizenVaccinationsIndicatorC6Data
	) {
		setIsLoadingCitizenWithVaccinations(true);
		setCitizenWithVaccinations(citizenVaccinations);
		setIsLoadingCitizenWithVaccinations(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedCitizensVaccinationsIndicatorC6();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformCitizensWithVaccinationsToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Vacinações - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformCitizensWithVaccinationsToGenericObjectArray(
		citizensWithVaccinations: DetailedCitizenVaccinationsIndicatorC6Data[]
	) {
		return citizensWithVaccinations.map(
			(citizenWithVaccinations) =>
				({
					Nome: citizenWithVaccinations.fullName,
					DN: dayjs(citizenWithVaccinations.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenWithVaccinations.birthdate),
					Documento: citizenWithVaccinations.cpf ?? citizenWithVaccinations.cns ?? "",
					Area: citizenWithVaccinations.area ?? "FA",
					"Ine Equipe": citizenWithVaccinations.healthTeam?.ine ?? "",
					"Nome Equipe": citizenWithVaccinations.healthTeam?.name ?? "",
					"Vacinas Influenza aplicadas": citizenWithVaccinations.vaccines?.length ?? 0,
					Status: getCitizenVaccinationStatusDescription(
						citizenWithVaccinations.vaccinationStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithVaccinations || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC6CitizensVaccinationsFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC6CitizensVaccinationsTable
							citizensVaccinations={paginatedCitizensVaccinations.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensVaccinations?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensVaccinations.pageSize}
								totalPages={paginatedCitizensVaccinations.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Vacinas aplicadas em idosos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC6CitizensVaccinationsHistory
							citizenWithVaccinations={citizenWithVaccinations}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
